import * as React from "react";
import { CalendarDays, Clock3, List as ListIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  abandonReschedule,
  cancelMeeting,
  listMeetings,
  rescheduleMeeting,
  resolveMeeting,
} from "@/api/client";
import { MeetingView, RescheduleRequest } from "@/api/types";
import { ConversationDetailDrawer } from "@/features/home/components/ConversationDetailDrawer";
import { ToastStack, useToasts } from "@/features/home/components/ToastStack";
import { useProfile } from "@/features/profile/ProfileContext";
import { browserTimeZone } from "@/features/profile/TimezoneField";
import { cn } from "@/lib/utils";

import { BackBar } from "@/components/BackBar";
import { PageTitle } from "@/components/PageTitle";
import { MeetingsCalendarView } from "./MeetingsCalendarView";
import {
  MeetingsFilter,
  MeetingsFilterChips,
} from "./MeetingsFilterChips";
import { MeetingsListView } from "./MeetingsListView";
import {
  collapseRescheduleChains,
  FOCUS_MEETING_PARAM,
  rescheduleGaveUpToast,
  ReschedulePick,
  toUiMeeting,
} from "./shared";

type ViewMode = "list" | "calendar";

export function MeetingsPage() {
  const [meetings, setMeetings] = React.useState<MeetingView[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<MeetingsFilter>("all");

  // The calendar is the default face of this page — clicking "Calendar" (or
  // landing with no ?view) shows the grid first, not the list. A deep link
  // (?meeting=<id>) opens onto it with that meeting selected; ?view=list is the
  // explicit opt-out. Reading the URL rather than mirroring it into state keeps
  // the link shareable and the back button honest.
  const [searchParams, setSearchParams] = useSearchParams();
  const view: ViewMode = searchParams.get("view") === "list" ? "list" : "calendar";
  const focusMeetingId = searchParams.get(FOCUS_MEETING_PARAM);

  // The negotiation drawer, opened from a meeting's #id chip.
  const [drawerId, setDrawerId] = React.useState<string | null>(null);

  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();

  // Neighbours of the open negotiation in its reschedule chain, so the drawer can
  // walk a meeting's history. Read off the RAW list: the collapsed one has, by
  // design, hidden the very rows we want to link to.
  const chain = React.useMemo(() => {
    if (!drawerId || !meetings) return undefined;
    const current = meetings.find((m) => m.conversation_id === drawerId);
    const next = meetings.find((m) => m.rescheduled_from === drawerId);
    return {
      previousId: current?.rescheduled_from ?? null,
      nextId: next?.conversation_id ?? null,
    };
  }, [drawerId, meetings]);

  const setView = React.useCallback(
    (next: ViewMode) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          // Calendar is the default, so it needs no param; list is the explicit
          // opt-out the URL carries.
          if (next === "list") params.set("view", "list");
          else params.delete("view");
          // Switching views by hand clears a stale focus; the highlight belongs
          // to the link that put it there, not to every later navigation.
          params.delete(FOCUS_MEETING_PARAM);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const reload = React.useCallback(() => {
    listMeetings()
      .then(setMeetings)
      .catch((e) => setError(String(e)));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      listMeetings()
        .then((data) => {
          if (!cancelled) setMeetings(data);
        })
        .catch((e) => {
          if (!cancelled) setError(String(e));
        });
    };
    refresh();
    const handle = window.setInterval(refresh, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  const handleCancel = React.useCallback(
    async (id: string) => {
      if (
        !window.confirm(
          "Cancel this meeting? The other party's agent will be notified.",
        )
      ) {
        return;
      }
      try {
        await cancelMeeting(id);
        reload();
      } catch (e) {
        setError(String(e));
      }
    },
    [reload],
  );

  const handleReschedule = React.useCallback(
    async (id: string, pick: ReschedulePick) => {
      try {
        const body: RescheduleRequest = {
          time_window: {
            start: pick.startDate,
            end: pick.endDate || pick.startDate,
          },
          time_zone: browserTimeZone(),
        };
        // A picked clock-time band rides along either way. `priority` is what
        // makes it STRICT: the agents may not stray from it, and the reschedule
        // fails outright if nobody can make it. Without it the band is only where
        // the search starts.
        if (pick.timeStart && pick.timeEnd) {
          body.time_of_day = {
            start: pick.timeStart,
            end: pick.timeEnd,
            priority: pick.strict === true,
          };
        }
        await rescheduleMeeting(id, body);
        reload();
      } catch (e) {
        setError(String(e));
      }
    },
    [reload],
  );

  const handleAbandonReschedule = React.useCallback(
    async (id: string) => {
      try {
        await abandonReschedule(id);
        reload();
      } catch (e) {
        setError(String(e));
      }
    },
    [reload],
  );

  const handleResolve = React.useCallback(
    async (id: string, startTimes: string[]) => {
      try {
        await resolveMeeting(id, startTimes);
        reload();
      } catch (e) {
        setError(String(e));
      }
    },
    [reload],
  );

  // A reschedule that books lands somewhere the user never chose, so say where.
  // Select it on the calendar the first time we see it accepted; the deep-link
  // machinery already handles jumping to its week and scrolling it into view.
  const announced = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!meetings) return;
    const landed = meetings.find(
      (m) =>
        m.state === "ACCEPTED" &&
        !!m.rescheduled_from &&
        !announced.current.has(m.conversation_id),
    );
    if (!landed) return;
    // Remember every accepted reschedule on the first pass, so a page opened long
    // after the fact doesn't yank the user to an old meeting.
    const first = announced.current.size === 0;
    meetings
      .filter((m) => m.state === "ACCEPTED" && !!m.rescheduled_from)
      .forEach((m) => announced.current.add(m.conversation_id));
    if (first) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("view", "calendar");
        params.set(FOCUS_MEETING_PARAM, landed.conversation_id);
        return params;
      },
      { replace: true },
    );
  }, [meetings, setSearchParams]);

  // When a reschedule someone ELSE asked for fails, their meeting is simply left
  // unchanged — so instead of the "you asked for…" banner (which is wrong for a
  // non-asker) we surface a transient, informative toast: your calendar was
  // checked, nothing worked, nothing moved. The asker sees the banner instead.
  const failuresSeen = React.useRef<Set<string> | null>(null);
  React.useEffect(() => {
    if (!meetings) return;
    const failedAttempts = meetings.filter(
      (m) =>
        (m.state === "FAILED" || m.state === "EXPIRED") && !!m.rescheduled_from,
    );
    if (failuresSeen.current === null) {
      // First load: baseline what already failed so we never toast history.
      failuresSeen.current = new Set(failedAttempts.map((m) => m.conversation_id));
      return;
    }
    for (const attempt of failedAttempts) {
      if (failuresSeen.current.has(attempt.conversation_id)) continue;
      failuresSeen.current.add(attempt.conversation_id);
      const original = meetings.find(
        (m) => m.conversation_id === attempt.rescheduled_from,
      );
      // The person who asked sees the persistent banner; skip the toast for them.
      if (original?.rescheduled_by_me) continue;
      const subject = original?.subject?.trim() || "one of your meetings";
      pushToast({
        tone: "info",
        title: "Someone tried to reschedule",
        body: rescheduleGaveUpToast(attempt.state, subject),
      });
    }
  }, [meetings, pushToast]);

  const now = React.useMemo(() => new Date(), []);
  const filtered = React.useMemo(() => {
    if (!meetings) return [];
    const rows = meetings
      .filter((m) => {
        // Default view hides cancelled rows and reschedule-away duplicates so
        // they don't clutter the schedule; both remain reachable via the
        // "Cancelled" chip. Explicit state filters show that state as-is.
        if (filter === "all") return !m.superseded && m.state !== "CANCELLED";
        return m.state === filter;
      })
      .map(toUiMeeting);
    // A meeting and the attempts to move it are one thing, not several. Only
    // collapse in the default view: an explicit state filter is asking to see
    // the raw negotiations in that state.
    return filter === "all" ? collapseRescheduleChains(rows) : rows;
  }, [meetings, filter]);

  return (
    <div className="mx-auto max-w-none space-y-4 px-6 py-6 font-comic-body">
      <BackBar />
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <PageTitle>My meetings</PageTitle>
          <p className="text-xs text-muted-foreground">
            Meetings from your synced calendar, or added by Scheda.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ViewToggle current={view} onChange={setView} />
          <MeetingsFilterChips current={filter} onChange={setFilter} />
        </div>
      </div>

      <CalendarTimezoneBanner />

      {error && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive-strong">
          Failed to load meetings: {error}
        </div>
      )}

      {!meetings && !error && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}

      {meetings && view === "list" && (
        <MeetingsListView
          meetings={filtered}
          now={now}
          onCancel={handleCancel}
          onReschedule={handleReschedule}
          onAbandonReschedule={handleAbandonReschedule}
          onResolve={handleResolve}
          onOpenNegotiation={setDrawerId}
        />
      )}
      {meetings && view === "calendar" && (
        // Cap + centre the calendar to the same width as the booking-page builder
        // (1140px) instead of stretching across the full viewport.
        <div className="mx-auto w-full max-w-[1140px]">
          <MeetingsCalendarView
            meetings={filtered}
            now={now}
            focusMeetingId={focusMeetingId}
            onCancel={handleCancel}
            onReschedule={handleReschedule}
            onAbandonReschedule={handleAbandonReschedule}
            onOpenNegotiation={setDrawerId}
            onBookingCancelled={(invitee) =>
              pushToast({
                tone: "success",
                title: "Booking cancelled",
                body: `${invitee} has been told.`,
              })
            }
            onBookingCancelFailed={(message) =>
              pushToast({ tone: "failure", title: "Couldn't cancel", body: message })
            }
          />
        </div>
      )}

      <ConversationDetailDrawer
        conversationId={drawerId}
        chain={chain}
        onNavigate={setDrawerId}
        onClose={() => setDrawerId(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

/** IANA name + short UTC offset, e.g. "Europe/Stockholm (GMT+2)". */
function tzLabel(tz: string): string {
  try {
    const part = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    return part ? `${tz} (${part})` : tz;
  } catch {
    return tz;
  }
}

/**
 * Makes it explicit which timezone the meeting times below are shown in, and
 * warns when the device's zone differs from the user's profile zone — so a user
 * who travelled isn't fooled into reading these as their home-local times.
 */
function CalendarTimezoneBanner() {
  const { state } = useProfile();
  const profileTz = state.status === "ready" ? state.profile.timezone : null;
  const deviceTz = browserTimeZone();
  const mismatch = !!profileTz && profileTz !== deviceTz;

  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-2xl border px-3 py-2 text-xs",
        mismatch
          ? "border-warning/30 bg-warning/10 text-warning-strong"
          : "border-border bg-muted/40 text-muted-foreground",
      )}
    >
      <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" />
      <span>
        Times shown in <strong className="font-semibold">{tzLabel(deviceTz)}</strong>,
        this device's timezone.
        {mismatch && (
          <>
            {" "}
            Your profile timezone is{" "}
            <strong className="font-semibold">{tzLabel(profileTz!)}</strong>; meetings
            are scheduled against that, but displayed here in your device's zone.
          </>
        )}
      </span>
    </div>
  );
}

function ViewToggle({
  current,
  onChange,
}: {
  current: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-full border border-border bg-muted/40 p-0.5 text-xs">
      <ToggleButton
        active={current === "list"}
        onClick={() => onChange("list")}
        icon={<ListIcon className="h-3.5 w-3.5" />}
        label="List"
      />
      <ToggleButton
        active={current === "calendar"}
        onClick={() => onChange("calendar")}
        icon={<CalendarDays className="h-3.5 w-3.5" />}
        label="Calendar"
      />
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 rounded-full px-3 py-1 font-display text-[11px] font-semibold uppercase tracking-wide transition-colors",
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
